import React from 'react';

interface RotatingIconProps {
  /** Static base icon class — never changes; direction is expressed by rotating the wrapper.
   *  e.g. "fal fa-chevron-down" (down↕up) or "fal fa-arrow-right-to-line" (expand↔collapse). */
  icon: string;
  /** When true, wrapper rotates to `openRotation` (default 180°). */
  isOpen?: boolean;
  openRotation?: number;
  closedRotation?: number;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  style?: React.CSSProperties;
  className?: string;
  ariaHidden?: boolean;
}

/**
 * VI-725 — React-safe directional FA icon for the v2 theme (mirrors jt-candidate-manager's
 * ChevronIcon). Renders ONE static `<i>` whose className never changes (so the theme converts it
 * to `<svg>` exactly once and React never re-touches it) and expresses open/closed by rotating the
 * wrapper `<span>` via CSS `transform`. `onClick` lives on the wrapper, so it survives the swap.
 */
const RotatingIcon: React.FC<RotatingIconProps> = ({
  icon,
  isOpen = false,
  openRotation = 180,
  closedRotation = 0,
  onClick,
  style,
  className,
  ariaHidden = true,
}) => {
  return (
    <span
      className={className}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      style={{
        display: 'inline-block',
        transition: 'transform 0.2s ease-in-out',
        transform: `rotate(${isOpen ? openRotation : closedRotation}deg)`,
        ...style,
      }}
    >
      <i className={icon} aria-hidden={ariaHidden} />
    </span>
  );
};

export default RotatingIcon;
